import React, { useCallback, memo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
//import styles from './MediaTab.module.css';
import { Caption, MediaItem, SpecialEffect } from '../../../types';
import styles from '../../../assets/Tab.module.css';
import { CropAdjustmentPanel } from './index';
import { QuickEffectsSection, TransitionsSection } from './components';
import { cleanupMediaItemResources } from '../../../utils/mediaItemCleanup'; // ✅ Import cleanup utility



const colorNameToHex: { [key: string]: string } = {
  red: '#ff0000',
  green: '#00ff00',
  blue: '#0000ff',
  yellow: '#ffff00',
  purple: '#800080',
  cyan: '#00ffff',
  black: '#000000',
  white: '#ffffff',
  gray: '#808080',
  orange: '#ffa500',
  brown: '#a52a2a',
  pink: '#ffc0cb',
};

interface EditSegmentTooltipProps {
  mediaMaker: MediaItem | null;
  isOpen: boolean;
  onClose: () => void;
  targetId: string | null;
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  onOpenAIVideoModal?: (imageUrl: string, prompt?: string) => void;
  onOpenUpscaleModal?: (videoUrl: string) => void;
  addAtCurrentTime?: boolean; // NEW: checkbox state
  currentTime?: number; // NEW: current timeline position
  mediaItems?: MediaItem[]; // NEW: all media items for conflict detection
  // NEW: Conversion props
  isConversionMode?: boolean;
  targetAspectRatio?: string | null;
  selectedMediaItem?: MediaItem | null;
  onResetMediaItem?: (itemId: string) => void;
}

const MediaTab: React.FC<EditSegmentTooltipProps> = ({
  mediaMaker,
  isOpen,
  onClose,
  targetId,
  setMediaItems,
  onOpenAIVideoModal,
  onOpenUpscaleModal,
  addAtCurrentTime = false, // NEW: default to false
  currentTime = 0, // NEW: default to 0
  mediaItems = [], // NEW: default to empty array
  isConversionMode, // NEW
  targetAspectRatio, // NEW
  selectedMediaItem, // NEW
  onResetMediaItem, // NEW
}) => {
  const { t } = useTranslation('mediaTab');
  
  if (!mediaMaker || !targetId) return null;

  // ✅ NEW: Add videoRef to access video element for cleanup
  const videoRefForCleanup = useRef<HTMLVideoElement | null>(null);

  // Try to get video ref from DOM
  useEffect(() => {
    const videoElement = document.querySelector('video');
    if (videoElement) {
      videoRefForCleanup.current = videoElement as HTMLVideoElement;
    }
  }, []);

  // State for the selected effect - ensure it gets the correct effect from the current media item
  const [selectedEffect, setSelectedEffect] = useState<SpecialEffect>(mediaMaker.effect || SpecialEffect.NONE);

  // Reset selected effect when media item changes to ensure we're showing the correct effect
  useEffect(() => {
    setSelectedEffect(mediaMaker.effect || SpecialEffect.NONE);
  }, [mediaMaker.id, mediaMaker.effect]);

  const handleCaptionUpdate = useCallback(
    (key: keyof Caption, value: number | string) => {
      setMediaItems((prevMediaItems) =>
        prevMediaItems.map((mediaItem) =>
          mediaItem.id === mediaMaker.id ? { ...mediaItem, [key]: value } : mediaItem
        )
      );
    },
    [mediaMaker.id, setMediaItems]
  );
  
  const handleEffectChange = useCallback(
    (effect: SpecialEffect) => {
      setSelectedEffect(effect);
      setMediaItems((prevMediaItems) =>
        prevMediaItems.map((mediaItem) =>
          mediaItem.id === mediaMaker.id ? { ...mediaItem, effect } : mediaItem
        )
      );
    },
    [mediaMaker.id, setMediaItems]
  );

  const handleStartTimeChange = (newStart: number) => {
    setMediaItems((prevMediaItems) => {
      const updated = [...prevMediaItems];
      const index = updated.findIndex((item) => item.id === mediaMaker.id);
      if (index === -1) return prevMediaItems;
      
      // Calculate the duration from the current item, default to 5s if NaN or negative
      const currentDuration = updated[index].end - updated[index].start;
      const safeDuration = (isNaN(currentDuration) || currentDuration <= 0) ? 5 : currentDuration;
      
      // Update start (location) and automatically calculate new end
      const newEnd = newStart + safeDuration;
      updated[index] = { 
        ...updated[index], 
        start: newStart,
        end: newEnd 
      };
      
      // Update the previous item's end to match our new start (if exists)
      if (index > 0) {
        updated[index - 1] = { ...updated[index - 1], end: newStart };
      }
      
      // Update the next item's start to match our new end (if exists)
      if (index < updated.length - 1) {
        updated[index + 1] = { ...updated[index + 1], start: newEnd };
      }
      
      return updated;
    });
  };

  const handleEndTimeChange = (newEnd: number) => {
    setMediaItems((prevMediaItems) => {
      const updated = [...prevMediaItems];
      const index = updated.findIndex((item) => item.id === mediaMaker.id);
      if (index === -1) return prevMediaItems;
      updated[index] = { ...updated[index], end: newEnd };
      if (index < updated.length - 1) {
        updated[index + 1] = { ...updated[index + 1], start: newEnd };
      }
      return updated;
    });
  };

  const refDoneButton = useRef<HTMLButtonElement>(null);
  
  // ✅ UPDATED: Add cleanup before deletion
  async function onMedaiItemDelete() {
    // Clean up resources before removing the item
    await cleanupMediaItemResources(mediaMaker!, videoRefForCleanup);
    
    setMediaItems((prevMediaItems) => {
      const index = prevMediaItems.findIndex(
        (item) => item.id === mediaMaker!.id
      );
      if (index === -1) return prevMediaItems;
      const newMediaItems = [...prevMediaItems];
      newMediaItems.splice(index, 1);
      console.log("✅ Item deleted after cleanup, current-> ", newMediaItems);
      if (refDoneButton.current) {
        refDoneButton.current.click();
      }
      return newMediaItems;
    });
  }

  function onMedaiItemDuplicate() {
    // Clone the current media item
    const clonedCurrentMediaItem: MediaItem = { ...mediaMaker! };
    
    console.log('🔄 Duplicate - started:', { addAtCurrentTime, currentTime, mediaLength: mediaMaker!.length });
    
    setMediaItems((prevMediaItems) => {
      // Calculate insertion position based on checkbox state
      const itemDuration = mediaMaker!.length || (mediaMaker!.end - mediaMaker!.start);
      const targetStart = addAtCurrentTime ? currentTime : (prevMediaItems.length > 0 ? prevMediaItems[prevMediaItems.length - 1].end : 0);
      let start = targetStart;
      let end = targetStart + itemDuration;

      console.log('🔄 Duplicate - calculating position:', { addAtCurrentTime, currentTime, targetStart, itemDuration });

      if (addAtCurrentTime) {
        console.log('==============> addAtCurrentTime',addAtCurrentTime);

        // Check for conflicts and find available space
        const hasConflict = prevMediaItems.some(item => 
          (start >= item.start && start < item.end) ||
          (end > item.start && end <= item.end) ||
          (start <= item.start && end >= item.end)
        );

        console.log('🔄 Duplicate - conflict check:', { hasConflict, start, end });
      }

      console.log('🔄 Duplicate - final position:', { start, end });

      // Update the cloned item with new position and ID
      clonedCurrentMediaItem.start = start;
      clonedCurrentMediaItem.end = end;
      clonedCurrentMediaItem.id = `${Date.now()}-duplicate-${mediaMaker?.name}`;
      
      // Insert and sort by start time
      const newItems = [...prevMediaItems, clonedCurrentMediaItem].sort((a, b) => a.start - b.start);
      return newItems;
    });
  }

  return (
    <div className={styles.container}>
      <div className={styles.stack}>
        {/* NEW: Crop Adjustment Panel - shows when conversion mode is active */}
        {isConversionMode && (
          <CropAdjustmentPanel
            isActive={isConversionMode}
            selectedMediaItem={selectedMediaItem || null}
            targetAspectRatio={targetAspectRatio}
            onReset={onResetMediaItem}
          />
        )}

        {/* Media Information Display */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label className={styles.label}>{t('mediaTab.labels.mediaName')}</label>
          <div className={styles.mediaInfo} style={{ 
            padding: '0.5rem', 
            background: '#333', 
            borderRadius: '4px', 
            color: '#e0e0e0',
            fontSize: '0.9rem'
          }}>
            {mediaMaker.name}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label className={styles.label}>{t('mediaTab.labels.type')}</label>
          <div className={styles.mediaInfo} style={{ 
            padding: '0.5rem', 
            background: '#333', 
            borderRadius: '4px', 
            color: '#e0e0e0',
            fontSize: '0.9rem',
            textTransform: 'capitalize'
          }}>
            {mediaMaker.type}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label className={styles.label}>{t('mediaTab.labels.duration')}</label>
          <div className={styles.mediaInfo} style={{ 
            padding: '0.5rem', 
            background: '#333', 
            borderRadius: '4px', 
            color: '#e0e0e0',
            fontSize: '0.9rem'
          }}>
            {(mediaMaker.end - mediaMaker.start).toFixed(1)}{t('mediaTab.duration.seconds')}
          </div>
        </div>

        {/* Location and End Time Display */}
        <div className={styles.startEndContainer} >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label className={styles.label}>{t('mediaTab.labels.location')}</label>
            <input
              type="number"
              value={mediaMaker.start}
              min="0"
              step="0.1"
              onChange={(e) => handleStartTimeChange(parseFloat(e.target.value))}
              className={`${styles.input} ${styles.inputSm}`}
            />
          </div>
        </div>

        {/* Effects Dropdown - Only show for images, not videos */}
        {mediaMaker.type !== 'video' && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label className={styles.label}>{t('mediaTab.labels.effect')}</label>
            <select
              value={selectedEffect}
              onChange={(e) => handleEffectChange(e.target.value as SpecialEffect)}
              className={styles.input}
              style={{ width: '100%', backgroundColor: '#2d2d2d', color: '#e0e0e0' }}
            >
              <option value={SpecialEffect.NONE}>{t('mediaTab.effects.none')}</option>
              <optgroup label={t('mediaTab.effects.motion')}>
                <option value={SpecialEffect.PANLEFT}>{t('mediaTab.effects.panLeft')}</option>
                <option value={SpecialEffect.PANLEFTTOP}>{t('mediaTab.effects.panLeftTop')}</option>
                <option value={SpecialEffect.PANRIGHT}>{t('mediaTab.effects.panRight')}</option>
                <option value={SpecialEffect.PANRIGHTTOP}>{t('mediaTab.effects.panRightTop')}</option>
                <option value={SpecialEffect.PANUP}>{t('mediaTab.effects.panUp')}</option>
                <option value={SpecialEffect.PANDOWN}>{t('mediaTab.effects.panDown')}</option>
                <option value={SpecialEffect.ZOOMIN}>{t('mediaTab.effects.zoomIn')}</option>
                <option value={SpecialEffect.ZOOMOUT}>{t('mediaTab.effects.zoomOut')}</option>
                <option value={SpecialEffect.ZOOMPANRIGHT}>{t('mediaTab.effects.zoomPanRight')}</option>
                <option value={SpecialEffect.ZOOMPANLEFT}>{t('mediaTab.effects.zoomPanLeft')}</option>
              </optgroup>
            </select>
          </div>
        )}

        {/* Visibility Toggle Switch */}
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '1rem', gap: '0.5rem' }}>
          <label className={styles.label}>{t('mediaTab.labels.visibility')}</label>
          
          <div 
            onClick={() => {
              setMediaItems((prevMediaItems) =>
                prevMediaItems.map((mediaItem) =>
                  mediaItem.id === mediaMaker.id 
                    ? { ...mediaItem, hidden: !mediaItem.hidden } 
                    : mediaItem
                )
              );
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem 1rem',
              background: mediaMaker.hidden ? '#2a2a2a' : '#2d2d2d',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              border: `1px solid ${mediaMaker.hidden ? '#444' : '#3a3a3a'}`,
              boxShadow: mediaMaker.hidden ? 'inset 0 2px 4px rgba(0,0,0,0.3)' : 'none'
            }}
          >
            {/* Left side - Icon and Label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: mediaMaker.hidden 
                  ? 'linear-gradient(135deg, #6b7280, #4b5563)' 
                  : 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem',
                transition: 'all 0.3s ease'
              }}>
                {mediaMaker.hidden ? '🙈' : '👁️'}
              </div>
              <span style={{
                color: mediaMaker.hidden ? '#888' : '#e0e0e0',
                fontSize: '0.95rem',
                fontWeight: '500',
                transition: 'color 0.3s ease',
                userSelect: 'none'
              }}>
                {mediaMaker.hidden ? t('mediaTab.visibility.hidden') : t('mediaTab.visibility.visible')}
              </span>
            </div>

            {/* Right side - Toggle Switch */}
            <div style={{
              position: 'relative',
              width: '48px',
              height: '24px',
              background: mediaMaker.hidden ? '#4b5563' : '#10b981',
              borderRadius: '12px',
              transition: 'background 0.3s ease',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <div style={{
                position: 'absolute',
                top: '2px',
                left: mediaMaker.hidden ? '2px' : '26px',
                width: '20px',
                height: '20px',
                background: 'white',
                borderRadius: '50%',
                transition: 'left 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }} />
            </div>
          </div>

          {/* Warning message when hidden */}
          {mediaMaker.hidden && (
            <div style={{
              fontSize: '0.75rem',
              color: '#999',
              fontStyle: 'italic',
              padding: '0.5rem',
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '6px',
              animation: 'fadeIn 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>⚠️</span>
              <span>{t('mediaTab.visibility.warningMessage')}</span>
            </div>
          )}
        </div>

        {/* Color Effect Section - Show for both images and videos */}
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '1rem', gap: '0.75rem' }}>
            <label className={styles.label}>{t('mediaTab.labels.colorEffect')}</label>
            
            <div style={{
              padding: '1rem',
              background: '#2d2d2d',
              borderRadius: '8px',
              border: '1px solid #3a3a3a'
            }}>
              {/* Color Picker */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.75rem'
              }}>
                <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                  {t('mediaTab.labels.tintColor')}
                </span>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="color"
                    value={mediaMaker.color || '#ffffff'}
                    onChange={(e) => {
                      setMediaItems((prevMediaItems) =>
                        prevMediaItems.map((mediaItem) =>
                          mediaItem.id === mediaMaker.id 
                            ? { ...mediaItem, color: e.target.value } 
                            : mediaItem
                        )
                      );
                    }}
                    style={{
                      width: '40px',
                      height: '40px',
                      border: '2px solid #555',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: mediaMaker.color || '#ffffff'
                    }}
                  />
                  
                  <div style={{
                    padding: '0.5rem 0.75rem',
                    background: '#1a1a1a',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    color: '#999',
                    minWidth: '70px',
                    textAlign: 'center'
                  }}>
                    {mediaMaker.color || 'None'}
                  </div>
                </div>
              </div>

              {/* Reset Button */}
              {mediaMaker.color && (
                <button
                  onClick={() => {
                    setMediaItems((prevMediaItems) =>
                      prevMediaItems.map((mediaItem) =>
                        mediaItem.id === mediaMaker.id 
                          ? { ...mediaItem, color: undefined } 
                          : mediaItem
                      )
                    );
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: '#3a3a3a',
                    color: '#e0e0e0',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#4a4a4a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#3a3a3a';
                  }}
                >
                  {t('mediaTab.colorEffect.resetButton')}
                </button>
              )}

              {/* Info Text */}
              <div style={{
                marginTop: '0.75rem',
                fontSize: '0.75rem',
                color: '#888',
                fontStyle: 'italic',
                lineHeight: '1.4'
              }}>
                {mediaMaker.color 
                  ? t('mediaTab.colorEffect.appliedInfo')
                  : t('mediaTab.colorEffect.selectInfo')}
              </div>
            </div>
            
            {/* Video Effects Buttons */}
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#2d2d2d',
              borderRadius: '8px',
              border: '1px solid #3a3a3a'
            }}>
              <QuickEffectsSection 
                mediaMaker={mediaMaker}
                setMediaItems={setMediaItems}
                styles={styles}
              />
            </div>
            
            {/* Transitions Section */}
            <TransitionsSection 
              mediaMaker={mediaMaker}
              setMediaItems={setMediaItems}
              allMediaItems={mediaItems}
            />
        </div>
        {/* Action Buttons */}
        <div className={styles.buttonGroup}>
          <button ref={refDoneButton} onClick={onClose} className={styles.button}>
            {t('mediaTab.buttons.done')}
          </button>
          <button onClick={onMedaiItemDuplicate} className={styles.button}>
            {t('mediaTab.buttons.duplicate')}
          </button>
          {/* AI Video button - only show for image media items */}
          {mediaMaker.type === 'image' && onOpenAIVideoModal && (
            <button 
              onClick={() => {
                // Extract prompt from media item name if it follows the "AI Generated: prompt..." pattern
                const extractedPrompt = mediaMaker.name?.startsWith('AI Generated: ') 
                  ? mediaMaker.name.replace('AI Generated: ', '').replace('...', '')
                  : undefined;
                
                onOpenAIVideoModal(mediaMaker.url || mediaMaker.src || '', extractedPrompt);
              }}
              className={styles.button}
              style={{ 
                background: 'linear-gradient(135deg, #8b5cf6, #e11d48)',
                color: 'white',
                border: 'none'
              }}
            >
              {t('mediaTab.buttons.aiVideo')}
            </button>
          )}
          {/* Upscale Video button - only show for video media items */}
          {mediaMaker.type === 'video' && onOpenUpscaleModal && (
            <button 
              onClick={() => {
                onOpenUpscaleModal(mediaMaker.url || mediaMaker.src || '');
              }}
              className={styles.button}
              style={{ 
                background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                color: 'white',
                border: 'none'
              }}
            >
              {t('mediaTab.buttons.upscaleVideo')}
            </button>
          )}
          <button onClick={onMedaiItemDelete} className={styles.button} style={{color:"#c86666"}}>
            {t('mediaTab.buttons.remove')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(MediaTab);
